import {
	useEffect,
	useRef,
	useState,
	Dispatch,
	SetStateAction,
	ChangeEvent,
	Fragment
} from 'react';
import {
	Button,
	H2,
	H4,
	Label,
	PODPanel,
	PODStatusChip,
	SkeletonLoader,
	Spacer,
	Text,
	Tile
} from '@components';
import styles from './MobilePODUpload.module.scss';
import {
	BUTTON_ICON_POS,
	BUTTON_TYPES,
	CARGO_TYPE_ID,
	CHIP_SIZE,
	FileUploadMeta,
	POD_STATUS,
	ShipmentTarget,
	SPACER_DIRECTION,
	TCargoToday,
	TEOSLoadMasterManifest,
	TShipmentCargo,
	YourLoadItem
} from '@types';
import { useGetCarrierFinancialDashboard, useGetLoadMasterManifestData } from '@api';
import { ChevronRight } from '@mui/icons-material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
	DATE_FORMATS, getCargoFullDescription, getCargoTypeLabel, parseAndFormatDate, rateCardTypeFromCargoTypeId
} from '@utils';
import { useWithImpersonation } from '@hooks';

type TMobilePODUploadProps = {
	shipmentTargets: ShipmentTarget[];
	onSetShipmentTargets: Dispatch<SetStateAction<ShipmentTarget[]>>;
	filesToUpload: Record<string, FileUploadMeta[]>;
	onSetFilesToUpload: Dispatch<SetStateAction<Record<string, FileUploadMeta[]>>>;
	onViewOnTheRoadLoadsClick: () => void;
	setIsPODUnavailable: Dispatch<SetStateAction<boolean>>;
	progressInfo: FileUploadMeta[];
	setProgressInfo: Dispatch<SetStateAction<FileUploadMeta[]>>;
	setIsBackArrowVisible: Dispatch<SetStateAction<boolean>>;
	isBackArrowClicked: boolean;
	setIsBackArrowClicked: Dispatch<SetStateAction<boolean>>;
	loadToUploadPOD: YourLoadItem | null;
	setManifestIdToUpload: Dispatch<SetStateAction<string | null>>;
}

export const MobilePODUpload = (props: TMobilePODUploadProps) => {
	const {
		shipmentTargets,
		onSetShipmentTargets,
		filesToUpload,
		onSetFilesToUpload,
		onViewOnTheRoadLoadsClick,
		setIsPODUnavailable,
		progressInfo,
		setProgressInfo,
		setIsBackArrowClicked,
		setIsBackArrowVisible,
		isBackArrowClicked,
		loadToUploadPOD,
		setManifestIdToUpload
	} = props;
	const [loads, setLoads] = useState<YourLoadItem[] | null>(null);
	const { callGetFinancialDashboard } = useGetCarrierFinancialDashboard();
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const { callGetLoadMasterManifestData } = useGetLoadMasterManifestData();
	const [currentManifest, setCurrentManifest] = useState<TEOSLoadMasterManifest | null>();
	const [showUploadPanel, setShowUploadPanel] = useState(false);
	const [activeLoad, setActiveLoad] = useState<YourLoadItem | null>(null);
	const singleUploader = useRef(null);
	const withImpersonation = useWithImpersonation('carrier');

	const fetchLoadMasterManifest = (loadId: string) => {
		callGetLoadMasterManifestData({ routes: [loadId] })
			.then((res) => {
				if (res?.data) {
					const manifest = res.data.data
					setIsBackArrowVisible(true);
					setCurrentManifest(manifest);
					setShowUploadPanel(true);
					onSetShipmentTargets([manifest as ShipmentTarget]);
				}
			})
			.catch((err) => console.log(err))
			.finally(() => setIsLoading(false));
	}

	const handleOnLoadClick = (load: YourLoadItem) => {
		setActiveLoad(load);
		setManifestIdToUpload(`${load.id}`);
		fetchLoadMasterManifest(load.master_manifest.id);
	}

	const handleSelectFiles = (event: ChangeEvent<HTMLInputElement>, id: string) => {
		if (event?.target?.files && event?.target?.files?.length > 0) {
			const newFiles: FileUploadMeta[] = Array.from(event.target.files).map((file) => ({
				u_id: crypto.randomUUID(),
				file,
				fileName: file.name,
				percentage: 0,
				is_start: false,
				is_success: false,
				is_error: false,
				error_message: '',
				pod_document_id: null,
			}));

			onSetFilesToUpload((prev) => ({
				...prev,
				[id]: [...(prev[id] || []), ...newFiles],
			}));
		}
		event.target.value = '';
	};

	const removeUploadFile = (fileId: string, targetId: string) => {
		const cloned = { ...filesToUpload };
		cloned[targetId] = cloned[targetId]?.filter((f) => f.u_id !== fileId);
		onSetFilesToUpload(cloned);
		setProgressInfo(progressInfo.filter((p) => p.u_id !== fileId));
	};

	const handlePODUnavailable = (podStatus: boolean) => {
		setIsPODUnavailable(podStatus);
	};

	const renderCargoDescription = (load: YourLoadItem) => {
		const cargos = load?.shipment?.cargos as TShipmentCargo[] | undefined;
		if (!cargos) return '';
		const cargoTypeId = load.shipment.cargo_type_id;
		const isCustomItemCargo = cargoTypeId === CARGO_TYPE_ID.CustomItem;
		return getCargoFullDescription(
			cargos,
			'pallet_qty',
			'pallet_space_quantity',
			'total_weight',
			isCustomItemCargo,
			cargoTypeId
		)
	}

	const handleViewDetailsClick = () => {
		window.location.href = withImpersonation(`/carrier/your-loads/${activeLoad?.master_manifest?.id}`);
	}

	useEffect(() => {
		callGetFinancialDashboard()
			.then((res) => {
				if (res?.data?.data) {
					setLoads(res.data.data);
				}
			})
			.catch((err) => console.log(err))
			.finally(() => setIsLoading(false))
	}, []);

	useEffect(() => {
		if (isBackArrowClicked) {
			setIsBackArrowClicked(false);
			setIsBackArrowVisible(false);
			setCurrentManifest(null);
			setShowUploadPanel(false);
			onSetShipmentTargets([]);
		}
	}, [isBackArrowClicked])

	useEffect(() => {
		if (!loads?.length) return;
		if (loadToUploadPOD) {
			const selectedLoad = loads.find((load) => load.id === loadToUploadPOD?.id)
			if (selectedLoad) handleOnLoadClick(selectedLoad);
		}
	}, [loadToUploadPOD, loads])

	return (
		<div>
			<div className={styles.heading}>
				<FileUploadOutlinedIcon />
				<H2>Upload Proof of Delivery</H2>
			</div>
			<Spacer size={20} />
			{
				showUploadPanel && currentManifest && activeLoad ?
					<>
						<Tile stretch>
							{
								isLoading ? <SkeletonLoader /> :
									<PODStatusChip
										status={activeLoad?.shipment?.pod_status?.short as POD_STATUS}
										size={CHIP_SIZE.SMALL}
									/>
							}
							<Spacer size={12} />
							<div className={styles.uploadPODContent}>
								{isLoading ? <SkeletonLoader /> :
									<Label>
										{(activeLoad.shipment?.cargos?.[0] as TCargoToday)?.pickup?.geo_address?.postcode?.suburb},
										{(activeLoad.shipment?.cargos?.[0] as TCargoToday)?.pickup?.geo_address?.postcode?.state}{' '}
										{(activeLoad.shipment?.cargos?.[0] as TCargoToday)?.pickup?.geo_address?.postcode?.postcode}
									</Label>
								}
								<span className={styles.uploadPODContentIcon}>
									<ArrowForwardIcon />
								</span>
								{isLoading ? <SkeletonLoader /> :
									<Label>
										{(activeLoad.shipment?.cargos?.[0] as TCargoToday)?.dropoff?.geo_address?.postcode?.suburb},
										{(activeLoad.shipment?.cargos?.[0] as TCargoToday)?.dropoff?.geo_address?.postcode?.state}{' '}
										{(activeLoad.shipment?.cargos?.[0] as TCargoToday)?.dropoff?.geo_address?.postcode?.postcode}
									</Label>
								}
							</div>
							{isLoading ? <>
								<Spacer size={4} />
								<SkeletonLoader width='30%' />
							</> :
								<Text text={activeLoad?.shipment?.reference} />
							}

							<Spacer size={4} />
							{isLoading ? <SkeletonLoader width='40%' /> :
								<Button
									type={BUTTON_TYPES.TERTIARY}
									onClick={handleViewDetailsClick}
									text={'View all load details'}
									Icon={ChevronRight}
									iconPos={BUTTON_ICON_POS.RIGHT}
								/>
							}
						</Tile>
						<Spacer size={20} />
						<PODPanel
							shipmentTargets={shipmentTargets}
							singleUploader={singleUploader}
							handleSelectFiles={handleSelectFiles}
							filesToUpload={filesToUpload}
							removeUploadFile={removeUploadFile}
							progressInfo={progressInfo}
							handlePODUnavailable={handlePODUnavailable}
							isPODUnavailableShow={false}
							manifestId={`${activeLoad.id}`}
						/>
					</>
					: <>
						<Text>
								You can upload Proof of Delivery (POD) documents once a load has been delivered.
						</Text>
						<Spacer size={20} />
						{
							isLoading && <div
								className={styles.loadContainer}
							>
								<div className={styles.load}>
									<div className={styles.podPanelHeading}>
										<SkeletonLoader width={'100px'} />
									</div>
									<Spacer size={8} />
									<SkeletonLoader width={'150px'} />
								</div>
							</div>
						}
						{
							(!isLoading && loads && loads.length > 0) &&
								<>
									{
										loads.map((load: YourLoadItem) => {
											return (
												<Fragment key={load.id}>
													<div
														className={styles.loadContainer}
														key={load.id}
														onClick={() => handleOnLoadClick(load)}
													>
														<div className={styles.load}>
															<div className={styles.podPanelHeading}>
																<Label>
																	{(load?.shipment?.cargos as TCargoToday[])?.[0]?.pickup?.geo_address?.postcode?.suburb}
																</Label>
																<ArrowForwardIcon />
																<Label>
																	{(load?.shipment?.cargos as TCargoToday[])?.[0]?.dropoff?.geo_address?.postcode?.suburb}
																</Label>
															</div>

															<Text>
																{load.shipment.reference}
															</Text>
															<div className={styles.detailsRow}>
																<Text>
																	{parseAndFormatDate(load.shipment.cargos[0].pickup_at, DATE_FORMATS.SHORT_DATE_NO_YEAR)}{' - '}
																	{parseAndFormatDate(load.shipment.cargos[0].drop_at, DATE_FORMATS.SHORT_DATE_NO_YEAR)}
																</Text>
																<Spacer
																	size={16}
																	divider
																	dir={SPACER_DIRECTION.VERTICAL}
																	dividerColor={'var(--neutral-03)'}
																/>
																<Text>
																	{getCargoTypeLabel(rateCardTypeFromCargoTypeId(load.shipment.cargo_type_id))}
																</Text>
																<Spacer
																	size={16}
																	divider
																	dir={SPACER_DIRECTION.VERTICAL}
																	dividerColor={'var(--neutral-03)'}
																/>
																<Text>
																	{renderCargoDescription(load)}
																</Text>
															</div>
														</div>
														<ChevronRight />
													</div>
													<Spacer size={8} />
												</Fragment>
											)
										})
									}
									<Tile stretch bgColor='var(--neutral-01)'>
										<H4>
											Not seeing a load here?
										</H4>
										<Text>
											It may not have been delivered yet.
										</Text>
										<Button
											type={BUTTON_TYPES.TERTIARY}
											onClick={onViewOnTheRoadLoadsClick}
											text={'View shipments on the road'}
											Icon={ChevronRight}
											iconPos={BUTTON_ICON_POS.RIGHT}
										/>
									</Tile>
								</>
						}
						{
							(!isLoading && loads && loads.length === 0) && <section className={styles.emptyContainer}>
								<div className={styles.illustration} />
								<Text text={`You don't have any loads expecting POD`} />
								<Spacer size={80} />
							</section>
						}
					</>

			}
		</div>
	);
}
